import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, CalendarDays, Sparkles } from "lucide-react";

const HERO_BACKGROUND = "/videos/hero-dancing-poster.jpg";
const HERO_IMAGE = "https://images.unsplash.com/photo-1763630053834-2dcdfce7e7e1?fm=jpg&q=80&w=1800&auto=format&fit=crop";

const lines = ["VYORA PILOT", "DAS ERSTE EVENT", "03.10.2026"];
const EVENT_TIMESTAMP = new Date("2026-10-03T20:00:00+02:00").getTime();

const getCountdown = () => {
  const difference = Math.max(0, EVENT_TIMESTAMP - Date.now());
  return {
    days: Math.floor(difference / 86_400_000),
    hours: Math.floor((difference / 3_600_000) % 24),
    minutes: Math.floor((difference / 60_000) % 60),
  };
};

const useCountdown = () => {
  const [countdown, setCountdown] = useState(getCountdown);
  useEffect(() => {
    const interval = setInterval(() => setCountdown(getCountdown()), 1000);
    return () => clearInterval(interval);
  }, []);
  return countdown;
};

export const Hero = ({ availability }) => {
  const { scrollYProgress } = useScroll();
  const countdown = useCountdown();
  const videoRef = useRef(null);
  const imageY = useTransform(scrollYProgress, [0, 0.35], [0, 110]);
  const imageScale = useTransform(scrollYProgress, [0, 0.35], [1, 1.08]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.playbackRate = 0.72;
    video.play().catch(() => {});
  }, []);

  return (
    <section id="top" className="relative min-h-screen overflow-hidden px-5 pb-16 pt-32 sm:px-8 lg:pt-36">
      <div className="absolute inset-0">
        <img src={HERO_BACKGROUND} alt="Tanzende Frauen bei einer Party" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <video ref={videoRef} data-testid="hero-background-video" className="absolute inset-0 h-full w-full object-cover opacity-55" autoPlay muted defaultMuted loop playsInline preload="auto" poster={HERO_BACKGROUND}>
          <source src="/videos/hero-dancing.webm" type="video/webm" />
          <source src="/videos/hero-dancing.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-[#09090D] via-[#09090D]/68 to-[#09090D]/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#09090D] via-transparent to-[#09090D]/80" />
      </div>
      <div className="hero-grid absolute inset-0 opacity-35" />
      <div className="absolute -left-40 top-24 h-96 w-96 rounded-full bg-[#FF4500]/10 blur-3xl" />
      <div className="absolute -right-32 top-1/3 h-[30rem] w-[30rem] rounded-full bg-[#E5C378]/10 blur-3xl" />
      <div className="relative mx-auto grid max-w-7xl items-end gap-12 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="relative z-10">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mb-8 flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-3 rounded-full border border-[#E5C378]/20 bg-white/[0.03] px-4 py-2 text-[#E5C378]">
              <Sparkles className="h-4 w-4" />
              <span className="font-mono text-xs uppercase tracking-[0.24em]">Welcome to the pilot</span>
            </span>
            <span className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-zinc-300">
              <CalendarDays className="h-4 w-4 text-[#E5C378]" />
              <span className="font-mono text-xs uppercase tracking-[0.24em]">{availability?.event_date || "03.10.2026"} • {availability?.event_name || "Pilot Event"}</span>
            </span>
          </motion.div>
          <motion.div data-testid="event-countdown" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.35 }} className="mb-8 grid max-w-xl grid-cols-3 gap-3">
            {[
              [countdown.days, "Tage", "countdown-days"],
              [countdown.hours, "Stunden", "countdown-hours"],
              [countdown.minutes, "Minuten", "countdown-minutes"],
            ].map(([value, label, testId]) => (
              <div key={testId} className="rounded-3xl border border-[#E5C378]/15 bg-white/[0.03] px-4 py-3 backdrop-blur-xl">
                <p data-testid={testId} className="font-syne text-2xl font-extrabold text-white sm:text-3xl">{String(value).padStart(2, "0")}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[#E5C378]">{label}</p>
              </div>
            ))}
          </motion.div>
          <h1 className="font-syne text-4xl font-extrabold leading-[1.02] tracking-tight text-[#F4F4F6] sm:text-5xl lg:text-6xl">
            {lines.map((line, index) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={index === 1 ? "block text-[#E5C378]" : "block"}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.18 + index * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.75 }} className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300 sm:text-lg">
            Limitierte Phase 2: Nur 50 neue Tickets für €10. Dein QR-Code erscheint sofort, kommt per E-Mail und wird am Eingang bezahlt.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.9 }} className="mt-10 flex flex-wrap items-center gap-4">
            <a data-testid="hero-register-button" href="#registrierung" className="rounded-full bg-[#E5C378] px-7 py-4 text-sm font-extrabold uppercase tracking-[0.16em] text-[#09090D] shadow-[0_0_44px_rgba(229,195,120,0.35)] transition-transform duration-200 hover:-translate-y-1">
              €10 Ticket sichern
            </a>
            <a data-testid="hero-manifesto-button" href="#manifest" className="group inline-flex items-center gap-3 rounded-full border border-white/10 px-6 py-4 text-sm font-bold text-zinc-200 transition-colors duration-200 hover:border-[#E5C378]/40 hover:text-[#E5C378]">
              Manifest lesen
              <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-1" />
            </a>
          </motion.div>

        </div>
        <motion.div style={{ y: imageY, scale: imageScale }} initial={{ opacity: 0, clipPath: "inset(20% 10% 20% 10% round 32px)" }} animate={{ opacity: 1, clipPath: "inset(0% 0% 0% 0% round 32px)" }} transition={{ duration: 1.15, delay: 0.35, ease: [0.22, 1, 0.36, 1] }} className="relative min-h-[28rem] overflow-hidden border border-[#E5C378]/20 shadow-2xl shadow-black/60 lg:min-h-[36rem]">
          <img src={HERO_IMAGE} alt="Tanzende Frau im Clublicht" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#09090D] via-transparent to-[#09090D]/20" />
          <div className="absolute bottom-6 left-6 right-6 rounded-3xl border border-white/10 bg-[#09090D]/70 p-5 backdrop-blur-xl">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#E5C378]">Nur 100 limitierte Tickets</p>
            <div className="mt-3 flex items-end justify-between gap-4">
              <p data-testid="hero-remaining-count" className="font-syne text-4xl font-extrabold text-white">{availability?.remaining ?? 100}</p>
              <p className="max-w-40 text-right text-sm text-zinc-400">QR-Check-in in Sekunden</p>
            </div>
            <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-white/10">
              <motion.div initial={{ width: 0 }} animate={{ width: `${((availability?.registered ?? 0) / (availability?.phase_limit || 100)) * 100}%` }} transition={{ duration: 1.2, delay: 1 }} className="h-full bg-[#E5C378]" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
